import InboundService from '@/services/inboundService';
import OutboundService from '@/services/outboundService';
import SustainabilityDataService from '@/services/sustainabilityDataService';
import InboundSustainabilityDataService from '@/services/inboundSustainabilityDataService';
import type { Inbound } from '@/types/inbound';
import { DateTime } from 'luxon';
import type { SustainabilityData } from '@/types/sustainabilityData';
import type { Outbound } from '@/types/outbound';
import { getEqual } from '@/helpers/filterList';
import type { SiteProduct } from '@/types/siteProduct';
import SiteProductService from '@/services/siteProductService';
import OutboundSustainabilityDataService from '@/services/outboundSustainabilityDataService';
import type { InboundSustainabilityData } from '@/types/inboundSustainabilityData';
import type { OutboundSustainabilityData } from '@/types/outboundSustainabilityData';
import type { MassBalance } from '@/types/massBalance';
import MassBalanceService from '@/services/massBalanceService';
import LocationService from '@/services/locationService';
import type { Location } from '@/types/location';

const inboundService = new InboundService();
const outboundService = new OutboundService();
const sustainabilityDataService = new SustainabilityDataService();
const inboundSustainabilityDataService = new InboundSustainabilityDataService();
const outboundSustainabilityDataService = new OutboundSustainabilityDataService();
const siteProductService = new SiteProductService();
const massBalanceService = new MassBalanceService();
const locationService = new LocationService();

const LOCATION_NO = '06K8';
const SUPPLIER_NO = '06K8';
const SUPPLIER_LOADING_NO = 'MB-ALTONA-40018313';
const FEEDSTOCK_IN_PRODCUT_NO = 'CANOLA OIL FEEDSTOCK';

export async function reset() {
    const inbounds: Inbound[] = (await inboundService.getFilteredList({ filter: { pageSize: 1000 } })).items; //.filter(({ number }) => number.startsWith('PU-') || number.startsWith('FOP-'));
    const sustainabilityDatas: SustainabilityData[] = (await sustainabilityDataService.getFilteredList({ filter: { pageSize: 1000 } })).items; //.filter(({ number }) => number.startsWith('SU-'));
    const outbounds: Outbound[] = (await outboundService.getFilteredList({ filter: { pageSize: 1000 } })).items; //.filter(({ number }) => number.startsWith('FFI-'));
    const inboundSustainabilityDatas: InboundSustainabilityData[] = (await inboundSustainabilityDataService.getFilteredList({ filter: { pageSize: 1000 } })).items; //.filter((is) => sustainabilityDatas.some(({ id }) => id === is.sustainabilityDataId));
    const outboundSustainabilityDatas: OutboundSustainabilityData[] = (await outboundSustainabilityDataService.getFilteredList({ filter: { pageSize: 1000 } })).items; //.filter((os) => sustainabilityDatas.some(({ id }) => id === os.sustainabilityDataId));
    const massBalances: MassBalance[] = (await massBalanceService.getFilteredList({ filter: { pageSize: 1000 } })).items; //.filter((mb) => mb.remarks === 'test');

    for (const { id } of outboundSustainabilityDatas) {
        await outboundSustainabilityDataService.delete(id);
    }
    for (const { id } of inboundSustainabilityDatas) {
        await inboundSustainabilityDataService.delete(id);
    }
    for (const { id } of massBalances) {
        await massBalanceService.delete(id);
    }
    for (const { number } of sustainabilityDatas) {
        await sustainabilityDataService.delete(number);
    }
    for (const { number } of outbounds) {
        await outboundService.delete(number);
    }
    for (const { number } of inbounds) {
        await inboundService.delete(number);
    }
}

export async function seed({ year, month, onlyFeedstockIn }: {
    year: number;
    month: number;
    onlyFeedstockIn?: boolean;
}) {
    const locations: Location[] = (await locationService.getFilteredList({ filter: { pageSize: 1000 } })).items;
    const supLocation: Location = locations.find((loc) => loc.number === SUPPLIER_LOADING_NO)!;
    const firstOfMonth = DateTime.now().set({ month, day: 1 });
    const lastOfMonth = DateTime.now().set({ month });
    lastOfMonth.set({ day: lastOfMonth.daysInMonth });

    const allSiteProducts = (await siteProductService.getFilteredListView({
        filter: {
            pageSize: 1000,
            filterList: [getEqual<SiteProduct>('feedstockFlag', 1)],
        }
    })).items;

    let siteProducts = allSiteProducts.filter(({ productNumber }) => (onlyFeedstockIn && productNumber !== FEEDSTOCK_IN_PRODCUT_NO) || !onlyFeedstockIn);

    siteProducts = [
        { productNumber: '126891', fopProductNumber: 'FINISHEDTANK' },
        { productNumber: '127998', fopProductNumber: 'NAPHTHA' },
    ];

    for (const siteProduct of siteProducts) {
        const inbounds: Inbound[] = [];
        const sustainabilityDatas: SustainabilityData[] = [];
        const outbounds: Outbound[] = [];
        inbounds.push(await inboundService.create({
            number: `PU-${year}-${month}-1-${siteProduct.productNumber}`,
            productNumber: siteProduct.productNumber,
            movementStatus: 100,
            movementType: 'PU',
            supplierPartnerNumber: SUPPLIER_NO,
            dischargeSiteNumber: LOCATION_NO,
            dischargeDate: firstOfMonth.plus({ days: 1 }).toISODate(), // irgendwann im Monat
            bioQuantity: 10000,
            totalQuantity: 10000,
            erpBookingDate: firstOfMonth.plus({ days: 1 }).toISODate(), // irgendwann im Monat
        })); // mehrere generieren
        inbounds.push(await inboundService.create({
            number: `PU-${year}-${month}-2-${siteProduct.productNumber}`,
            productNumber: siteProduct.productNumber,
            movementStatus: 100,
            movementType: 'PU',
            supplierPartnerNumber: SUPPLIER_NO,
            dischargeSiteNumber: LOCATION_NO,
            dischargeDate: firstOfMonth.plus({ days: 2 }).toISODate(), // irgendwann im Monat
            bioQuantity: 20000,
            totalQuantity: 20000,
            erpBookingDate: firstOfMonth.plus({ days: 2 }).toISODate(), // irgendwann im Monat
        })); // mehrere generieren

        sustainabilityDatas.push(await sustainabilityDataService.create({
            number: `SU-${year}-${month}-1-${siteProduct.productNumber}`,
            // status: 100,
            movementStatus: 100,
            eligibleFlag: true,
            productNumber: siteProduct.productNumber,
            rawMaterialProductNumber: siteProduct.productNumber,
            dischargeDate: firstOfMonth.plus({ days: 3 }).toISODate(), // irgendwann im Monat
            loadingDate: firstOfMonth.plus({ days: 3 }).toISODate(), // irgendwann im Monat
            volume: 10000, // wird für die Berechnung verwendet
        })); // mehrere generieren
        sustainabilityDatas.push(await sustainabilityDataService.create({
            number: `SU-${year}-${month}-2-${siteProduct.productNumber}`,
            status: 100,
            movementStatus: 100,
            eligibleFlag: true,
            productNumber: siteProduct.productNumber,
            rawMaterialProductNumber: siteProduct.productNumber,
            supplierLoadingSiteNumber: supLocation.number,
            supplierLoadingSiteVersion: supLocation.version,
            dischargeDate: firstOfMonth.plus({ days: 4 }).toISODate(), // irgendwann im Monat
            loadingDate: firstOfMonth.plus({ days: 4 }).toISODate(), // irgendwann im Monat
            volume: 20000, // wird für die Berechnung verwendet
        })); // mehrere generieren

        // create InboundSus Relations
        inboundSustainabilityDataService.create({
            inboundId: inbounds[0].id,
            sustainabilityDataId: sustainabilityDatas[0].id,
        });
        inboundSustainabilityDataService.create({
            inboundId: inbounds[1].id,
            sustainabilityDataId: sustainabilityDatas[1].id,
        });

        outbounds.push(await outboundService.create({
            number: `FFI-${year}-${month}-1-${siteProduct.productNumber}-${FEEDSTOCK_IN_PRODCUT_NO}`,
            recipientPartnerNumber: LOCATION_NO,
            productNumber: FEEDSTOCK_IN_PRODCUT_NO,
            movementType: 'FFI',
            movementStatus: 100,
            loadingSiteNumber: LOCATION_NO,
            loadingDate: firstOfMonth.plus({ days: 5 }).toISODate(), // irgendwann im Monat
            quantityLoaded: 8000,
            quantityLoadedKg: 8000,
            quantityLoadedL15: 8000,
            erpBookingDate: firstOfMonth.plus({ days: 5 }).toISODate(), // irgendwann im Monat
        }));
        outbounds.push(await outboundService.create({
            number: `FFI-${year}-${month}-2-${siteProduct.productNumber}-${FEEDSTOCK_IN_PRODCUT_NO}`,
            recipientPartnerNumber: LOCATION_NO,
            productNumber: FEEDSTOCK_IN_PRODCUT_NO,
            movementType: 'FFI',
            movementStatus: 100,
            loadingSiteNumber: LOCATION_NO,
            loadingDate: firstOfMonth.plus({ days: 6 }).toISODate(), // irgendwann im Monat
            quantityLoaded: 5000,
            quantityLoadedKg: 5000,
            quantityLoadedL15: 5000,
            erpBookingDate: firstOfMonth.plus({ days: 6 }).toISODate(), // irgendwann im Monat
        }));
        outbounds.push(await outboundService.create({
            number: `FFI-${year}-${month}-3-${siteProduct.productNumber}-${FEEDSTOCK_IN_PRODCUT_NO}`,
            recipientPartnerNumber: LOCATION_NO,
            productNumber: FEEDSTOCK_IN_PRODCUT_NO,
            movementType: 'FFI',
            movementStatus: 100,
            loadingSiteNumber: LOCATION_NO,
            loadingDate: firstOfMonth.plus({ days: 7 }).toISODate(), // irgendwann im Monat
            quantityLoaded: 6000,
            quantityLoadedKg: 6000,
            quantityLoadedL15: 6000,
            erpBookingDate: firstOfMonth.plus({ days: 7 }).toISODate(), // irgendwann im Monat
        }));


        inbounds.push(await inboundService.create({
            number: `FOP-${year}-${month}-1-${siteProduct.productNumber}-${siteProduct.fopProductNumber}`,
            productNumber: siteProduct.fopProductNumber,
            movementStatus: 100,
            movementType: 'FOP',
            supplierPartnerNumber: SUPPLIER_NO,
            dischargeSiteNumber: LOCATION_NO,
            dischargeDate: firstOfMonth.plus({ days: 8 }).toISODate(), // irgendwann im Monat
            bioQuantity: 5600,
            totalQuantity: 5600,
            erpBookingDate: firstOfMonth.plus({ days: 8 }).toISODate(), // irgendwann im Monat
        })); // mehrere generieren
        inbounds.push(await inboundService.create({
            number: `FOP-${year}-${month}-2-${siteProduct.productNumber}-${siteProduct.fopProductNumber}`,
            productNumber: siteProduct.fopProductNumber,
            movementStatus: 100,
            movementType: 'FOP',
            supplierPartnerNumber: SUPPLIER_NO,
            dischargeSiteNumber: LOCATION_NO,
            dischargeDate: firstOfMonth.plus({ days: 9 }).toISODate(), // irgendwann im Monat
            bioQuantity: 3500,
            totalQuantity: 3500,
            erpBookingDate: firstOfMonth.plus({ days: 9 }).toISODate(), // irgendwann im Monat
        })); // mehrere generieren
        inbounds.push(await inboundService.create({
            number: `FOP-${year}-${month}-3-${siteProduct.productNumber}-${siteProduct.fopProductNumber}`,
            productNumber: siteProduct.fopProductNumber,
            movementStatus: 100,
            movementType: 'FOP',
            supplierPartnerNumber: SUPPLIER_NO,
            dischargeSiteNumber: LOCATION_NO,
            dischargeDate: firstOfMonth.plus({ days: 10 }).toISODate(), // irgendwann im Monat
            bioQuantity: 4200,
            totalQuantity: 4200,
            erpBookingDate: firstOfMonth.plus({ days: 10 }).toISODate(), // irgendwann im Monat
        })); // mehrere generieren
    };
}
